/**
 * Utility functions for the application.
 */

import { format, parseISO, startOfDay, isBefore } from 'date-fns';
import type {
  DateFrequency,
  OrganizationDeliverableResult,
  PercentileData,
  ProcessedDeliverableResult,
  SimulationFormValues,
  SimulationFormData,
  OrganizationMonteCarloRequest,
  DeliverableFormValues,
  SimulationResponse,
  DependencyMap,
  DeliverableInfo,
  TeamResponse,
  PathNodeInfo,
  FeatureTeamRef,
  TeamFormValues,
} from '@/types/api';
import { parseThroughputData } from './validation';

/**
 * Compare two values for equality, using deep comparison for arrays/objects.
 * Handles primitives via ===, arrays/objects via JSON.stringify.
 */
export function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return a === b;
  if (a === undefined || b === undefined) return a === b;
  if (Array.isArray(a) && Array.isArray(b)) {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  if (typeof a === 'object' && typeof b === 'object') {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
}

/**
 * Format a date string for display.
 */
export function formatDate(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'yyyy-MM-dd');
  } catch {
    return dateStr;
  }
}

/**
 * Find percentile date from a distribution.
 * @param distribution Sorted array of date frequencies
 * @param percentile Target percentile (0-1)
 */
export function findPercentileDate(distribution: DateFrequency[], percentile: number): string | null {
  if (distribution.length === 0) return null;

  for (const entry of distribution) {
    if (entry.cumulative_probability >= percentile) {
      return entry.date;
    }
  }

  return distribution[distribution.length - 1]?.date ?? null;
}

/**
 * Calculate percentiles for a deliverable's finish distribution.
 */
function calculatePercentiles(distribution: DateFrequency[]): PercentileData {
  return {
    p50: findPercentileDate(distribution, 0.5),
    p85: findPercentileDate(distribution, 0.85),
    p95: findPercentileDate(distribution, 0.95),
  };
}

/**
 * Cumulative probability of finishing on or before `dueDate`.
 *
 * The distribution is sorted ascending by date and only carries buckets for
 * dates that actually had finishes — it has no leading zero-probability entry.
 * "P(finished by dueDate)" is therefore the cumulative probability of the LAST
 * bucket whose date is on or before `dueDate`; when no bucket qualifies (the
 * due date precedes every simulated finish) the answer is 0, not the first
 * bucket's mass.
 *
 * This is the single source of truth the timeline and the distribution view
 * both read, so the two can never disagree at the 0% boundary (a due date
 * earlier than any finish reads 0% on both, not 0% on one and ~11% on the
 * other).
 *
 * @param distribution - Sorted-by-date finish buckets from the Monte Carlo run
 * @param dueDate - Target date as an ISO `yyyy-MM-dd` string
 * @returns Cumulative probability in [0, 1], or null for an empty distribution
 */
export function cumulativeProbabilityByDate(distribution: DateFrequency[], dueDate: string): number | null {
  if (distribution.length === 0) return null;

  let cumProbAtDueDate = 0;
  for (const entry of distribution) {
    if (entry.date <= dueDate) {
      cumProbAtDueDate = entry.cumulative_probability;
    } else {
      break;
    }
  }
  return cumProbAtDueDate;
}

/**
 * Resolve the effective simulation start date.
 *
 * A null/undefined `start_date` means "start today" — the backend defaults an
 * absent start_date to the runtime date (see api/db.rs), so the frontend must
 * mirror that fallback to compare due dates against the same anchor.
 */
export function resolveSimulationStart(startDate: string | null | undefined): Date {
  return startDate ? startOfDay(parseISO(startDate)) : startOfDay(new Date());
}

/**
 * True when a due date falls strictly before the simulation start.
 *
 * This flags a deadline that already passed before the forecast could begin —
 * an impossible-to-meet / stale date that no amount of throughput can hit. It
 * is distinct from "at risk" (a due date the forecast might miss) and is the
 * single source of truth shared by the timeline label column and the result
 * cards, so both views agree on when to warn.
 */
export function isDueBeforeStart(dueDate: string | Date | null | undefined, start: Date): boolean {
  if (!dueDate) return false;
  const due = startOfDay(typeof dueDate === 'string' ? parseISO(dueDate) : dueDate);
  return isBefore(due, start);
}

/**
 * Calculate risk probability (probability of finishing after due date).
 */
function calculateRiskProbability(distribution: DateFrequency[], dueDate: string): number | null {
  const cumProbAtDueDate = cumulativeProbabilityByDate(distribution, dueDate);
  if (cumProbAtDueDate === null) return null;

  // Risk is probability of NOT finishing by due date.
  return 1 - cumProbAtDueDate;
}

/**
 * Process deliverable results for visualization.
 */
export function processDeliverableResults(results: OrganizationDeliverableResult[]): ProcessedDeliverableResult[] {
  return results.map((result) => {
    const finishPercentiles = calculatePercentiles(result.finished);
    let atRisk = false;
    let riskProbability: number | null = null;

    if (result.due_date) {
      riskProbability = calculateRiskProbability(result.finished, result.due_date);
      // Consider at risk if >15% probability of missing deadline
      atRisk = riskProbability !== null && riskProbability > 0.15;
    }

    return {
      ...result,
      finishPercentiles,
      atRisk,
      riskProbability,
    };
  });
}

/**
 * Reorder deliverable results to match the configuration order.
 * Falls back gracefully when names are missing from the order lists.
 */
export function reorderDeliverableResults(
  deliverables: OrganizationDeliverableResult[],
  teamOrder: string[],
  teamDeliverableOrder: Record<string, string[]>,
): OrganizationDeliverableResult[] {
  const teamIndex = (team: string) => teamOrder.indexOf(team);

  return [...deliverables]
    .map((d, idx) => ({ d, idx }))
    .sort((a, b) => {
      const aTeamIdx = teamIndex(a.d.team_name);
      const bTeamIdx = teamIndex(b.d.team_name);

      if (aTeamIdx !== -1 || bTeamIdx !== -1) {
        if (aTeamIdx === -1) return 1;
        if (bTeamIdx === -1) return -1;
        if (aTeamIdx !== bTeamIdx) return aTeamIdx - bTeamIdx;
      }

      const teamOrderList = teamDeliverableOrder[a.d.team_name];
      if (teamOrderList && teamOrderList.length > 0) {
        const aDeliverableIdx = teamOrderList.indexOf(a.d.deliverable_name);
        const bDeliverableIdx = teamOrderList.indexOf(b.d.deliverable_name);
        if (aDeliverableIdx !== -1 || bDeliverableIdx !== -1) {
          if (aDeliverableIdx === -1) return 1;
          if (bDeliverableIdx === -1) return -1;
          if (aDeliverableIdx !== bDeliverableIdx) return aDeliverableIdx - bDeliverableIdx;
        }
      }

      // Fallback to original order to keep sort stable when no hints are available
      return a.idx - b.idx;
    })
    .map(({ d }) => d);
}

/**
 * Convert validated form output to an API request (for legacy one-off simulation).
 *
 * Takes the schema OUTPUT shape (`SimulationFormData`) — i.e. post-validation
 * values where numbers are coerced and defaults applied — because it's only ever
 * called with the data delivered by react-hook-form's `handleSubmit`.
 *
 * NOTE: This still uses feature_name strings for the legacy /organization-monte-carlo endpoint.
 */
export function formValuesToRequest(values: SimulationFormData): OrganizationMonteCarloRequest {
  return {
    teams: values.teams.map((team) => ({
      name: team.name,
      throughput_data: parseThroughputData(team.throughput_data),
      throughput_scale: team.throughput_scale,
      deliverables: team.deliverables.map((deliverable) => {
        // Apply defaults for legacy endpoint: min → 0, max → min
        const unknown_min = deliverable.unknown_min ?? 0;
        const unknown_max = deliverable.unknown_max ?? unknown_min;
        return {
          // Legacy endpoint still uses feature_name strings - use deliverable name if no feature
          feature_name: null, // Simplified - database simulations use feature_id now
          name: deliverable.name,
          unfinished_work_items: deliverable.unfinished_work_items,
          unknown_min,
          unknown_max,
          predecessors: deliverable.predecessors.filter((p) => p.trim().length > 0).map((p) => ({ deliverable: p })),
          due_date: deliverable.due_date || null,
          available_to_start_min: deliverable.available_to_start_min || null,
          available_to_start_max: deliverable.available_to_start_max || null,
        };
      }),
      wip: team.wip,
    })),
    num_simulations: values.num_simulations,
    start_date: values.start_date,
    seed: values.seed ? parseInt(values.seed, 10) : null,
  };
}

/**
 * Default team color palette for fallback when no custom color is set.
 * 30 distinct, visually distinguishable colors organized by hue.
 */
export const TEAM_COLOR_PALETTE = [
  // Row 1: Reds & Pinks
  '#ef4444', // red
  '#f43f5e', // rose
  '#ec4899', // pink
  '#d946ef', // fuchsia
  '#a855f7', // purple
  '#8b5cf6', // violet
  // Row 2: Blues
  '#6366f1', // indigo
  '#3b82f6', // blue
  '#0ea5e9', // sky
  '#06b6d4', // cyan
  '#14b8a6', // teal
  '#10b981', // emerald
  // Row 3: Greens & Yellows
  '#22c55e', // green
  '#84cc16', // lime
  '#eab308', // yellow
  '#f59e0b', // amber
  '#f97316', // orange
  '#fb923c', // orange-light
  // Row 4: Neutrals & Pastels
  '#64748b', // slate
  '#78716c', // stone
  '#a1a1aa', // zinc
  '#f472b6', // pink-light
  '#c084fc', // purple-light
  '#818cf8', // indigo-light
  // Row 5: More variations
  '#38bdf8', // sky-light
  '#34d399', // emerald-light
  '#a3e635', // lime-light
  '#fbbf24', // amber-light
  '#fb7185', // rose-light
  '#c026d3', // fuchsia-dark
];

/**
 * Get team color - uses stored color if available, otherwise falls back to index-based palette.
 */
export function getTeamColor(index: number, storedColor?: string | null): string {
  // Use stored color if provided
  if (storedColor) {
    return storedColor;
  }
  // Fall back to index-based color
  return TEAM_COLOR_PALETTE[index % TEAM_COLOR_PALETTE.length] ?? TEAM_COLOR_PALETTE[0] ?? '#636df1';
}

/**
 * Deliverable option for predecessor selection dropdown.
 */
export interface DeliverableOption {
  id: string;
  teamName: string;
  teamColor?: string; // Team color for visual identification
  deliverableName: string;
  featureName?: string; // Feature name if assigned
  priority?: number; // 1-based position within team
  displayLabel: string; // "Team|Deliverable" format for display
  sourceId?: string | null; // External tracker key (e.g. "PROJ-123"); shared across an epic's per-team slices
}

/**
 * Get all deliverable options for dependency selection.
 * Returns UUIDs with display labels, team colors, and feature names.
 */
export function getAllDeliverableOptions(
  teams: { name: string; color?: string; deliverables: DeliverableFormValues[] }[],
  features?: { id?: string; name: string }[],
): DeliverableOption[] {
  const options: DeliverableOption[] = [];

  // Build feature lookup map
  const featureLookup = new Map<string, string>();
  if (features) {
    for (const feature of features) {
      if (feature.id) {
        featureLookup.set(feature.id, feature.name);
      }
    }
  }

  teams.forEach((team, teamIndex) => {
    // Use stored color or fallback to index-based color
    const teamColor = team.color || getTeamColor(teamIndex);

    for (let di = 0; di < team.deliverables.length; di++) {
      const deliverable = team.deliverables[di]!;
      if (deliverable.id) {
        const featureName = deliverable.feature_id ? featureLookup.get(deliverable.feature_id) : undefined;

        options.push({
          id: deliverable.id,
          teamName: team.name,
          teamColor,
          deliverableName: deliverable.name,
          featureName,
          priority: di + 1,
          displayLabel: `${team.name}|${deliverable.name}`,
          sourceId: deliverable.source_id ?? null,
        });
      }
    }
  });

  return options;
}

/**
 * Build a dependency map from simulation teams.
 *
 * Creates bidirectional relationship mappings:
 * - predecessors: deliverable ID → IDs of deliverables that must complete first
 * - dependents: deliverable ID → IDs of deliverables that wait for this one
 *
 * @example
 * // Given: Deliverable "B" has predecessor "A"
 * // Result: predecessors["B"] = ["A"], dependents["A"] = ["B"]
 */
export function buildDependencyMap(teams: TeamResponse[]): DependencyMap {
  const predecessors: Record<string, string[]> = {};
  const dependents: Record<string, string[]> = {};

  // Initialize maps for all deliverables
  for (const team of teams) {
    for (const deliverable of team.deliverables) {
      predecessors[deliverable.id] = [];
      dependents[deliverable.id] = [];
    }
  }

  // Build predecessor and dependent relationships
  for (const team of teams) {
    for (const deliverable of team.deliverables) {
      // predecessors array contains IDs of deliverables that block this one
      for (const predecessorId of deliverable.predecessors) {
        // Add to this deliverable's predecessors
        predecessors[deliverable.id]!.push(predecessorId);
        // Add this deliverable to the predecessor's dependents
        if (dependents[predecessorId]) {
          dependents[predecessorId].push(deliverable.id);
        }
      }
    }
  }

  return { predecessors, dependents };
}

/**
 * Build a lookup map of deliverable information for display in popovers.
 *
 * Returns a Map for O(1) lookups when rendering dependency lists.
 */
export function buildDeliverableLookup(teams: TeamResponse[]): Map<string, DeliverableInfo> {
  const lookup = new Map<string, DeliverableInfo>();

  for (const team of teams) {
    for (const deliverable of team.deliverables) {
      lookup.set(deliverable.id, {
        id: deliverable.id,
        name: deliverable.name,
        teamName: team.name,
        featureName: deliverable.feature_name ?? null,
        sourceId: deliverable.source_id ?? null,
      });
    }
  }

  return lookup;
}

/**
 * Traverse all predecessors (blockers) recursively using BFS with depth tracking.
 *
 * Returns a Map of deliverable ID to depth (number of hops from start).
 * The starting deliverable has depth 0.
 *
 * @example
 * // Given: C depends on B, B depends on A
 * // getBlockingChainWithDepth("C", map) returns Map{"C"=>0, "B"=>1, "A"=>2}
 */
export function getBlockingChainWithDepth(startId: string, dependencyMap: DependencyMap): Map<string, number> {
  const depths = new Map<string, number>();
  const queue: Array<{ id: string; depth: number }> = [{ id: startId, depth: 0 }];

  while (queue.length > 0) {
    const { id: current, depth } = queue.shift()!;
    if (depths.has(current)) continue;
    depths.set(current, depth);

    const predecessors = dependencyMap.predecessors[current] ?? [];
    for (const predId of predecessors) {
      if (!depths.has(predId)) {
        queue.push({ id: predId, depth: depth + 1 });
      }
    }
  }

  return depths;
}

/**
 * Traverse all dependents (blocked items) recursively using BFS with depth tracking.
 *
 * Returns a Map of deliverable ID to depth (number of hops from start).
 * The starting deliverable has depth 0.
 *
 * @example
 * // Given: A blocks B, B blocks C
 * // getFollowingChainWithDepth("A", map) returns Map{"A"=>0, "B"=>1, "C"=>2}
 */
export function getFollowingChainWithDepth(startId: string, dependencyMap: DependencyMap): Map<string, number> {
  const depths = new Map<string, number>();
  const queue: Array<{ id: string; depth: number }> = [{ id: startId, depth: 0 }];

  while (queue.length > 0) {
    const { id: current, depth } = queue.shift()!;
    if (depths.has(current)) continue;
    depths.set(current, depth);

    const dependents = dependencyMap.dependents[current] ?? [];
    for (const depId of dependents) {
      if (!depths.has(depId)) {
        queue.push({ id: depId, depth: depth + 1 });
      }
    }
  }

  return depths;
}

/**
 * Get the full dependency path with direction and depth info for each node.
 *
 * Returns a Map where each deliverable ID maps to its PathNodeInfo:
 * - direction: "blocking" (upstream), "following" (downstream), or "selected"
 * - depth: number of hops from the selected item
 *
 * @example
 * // Given: A → B → C → D (arrows = "blocks"), selected = C
 * // Returns: Map{
 * //   "A" => { direction: "blocking", depth: 2 },
 * //   "B" => { direction: "blocking", depth: 1 },
 * //   "C" => { direction: "selected", depth: 0 },
 * //   "D" => { direction: "following", depth: 1 }
 * // }
 */
export function getDependencyPathWithInfo(startId: string, dependencyMap: DependencyMap): Map<string, PathNodeInfo> {
  const pathInfo = new Map<string, PathNodeInfo>();

  // Get blocking chain (upstream) with depths
  const blockingDepths = getBlockingChainWithDepth(startId, dependencyMap);

  // Get following chain (downstream) with depths
  const followingDepths = getFollowingChainWithDepth(startId, dependencyMap);

  // Add blocking nodes (exclude the start node, it will be added as "selected")
  for (const [id, depth] of blockingDepths) {
    if (id === startId) continue;
    pathInfo.set(id, { direction: 'blocking', depth });
  }

  // Add following nodes (exclude the start node)
  for (const [id, depth] of followingDepths) {
    if (id === startId) continue;
    pathInfo.set(id, { direction: 'following', depth });
  }

  // Add the selected node itself
  pathInfo.set(startId, { direction: 'selected', depth: 0 });

  return pathInfo;
}

// Legacy functions for backward compatibility

/**
 * Traverse all predecessors (blockers) recursively using BFS.
 * @deprecated Use getBlockingChainWithDepth for depth-aware traversal
 */
export function getFullBlockingChain(startId: string, dependencyMap: DependencyMap): Set<string> {
  return new Set(getBlockingChainWithDepth(startId, dependencyMap).keys());
}

/**
 * Traverse all dependents (blocked items) recursively using BFS.
 * @deprecated Use getFollowingChainWithDepth for depth-aware traversal
 */
export function getFullDependentChain(startId: string, dependencyMap: DependencyMap): Set<string> {
  return new Set(getFollowingChainWithDepth(startId, dependencyMap).keys());
}

/**
 * Get the full dependency path in both directions.
 * @deprecated Use getDependencyPathWithInfo for direction and depth info
 */
export function getFullDependencyPath(startId: string, dependencyMap: DependencyMap): Set<string> {
  return new Set(getDependencyPathWithInfo(startId, dependencyMap).keys());
}

/**
 * Convert a SimulationResponse to SimulationFormValues.
 * Used when loading a simulation from the database for editing.
 * Includes all UUIDs so the form can make targeted API updates.
 *
 * IMPORTANT: Do not use type assertions (`as`) here. The generated API types
 * should flow correctly. If TypeScript complains about a missing field,
 * update the OpenAPI spec in the backend rather than adding assertions.
 */
export function simulationResponseToFormValues(sim: SimulationResponse): SimulationFormValues {
  return {
    name: sim.name,
    description: sim.description ?? '',
    features: sim.features.map((f) => ({
      id: f.id,
      name: f.name,
      source_id: f.source_id ?? null,
      source_url: f.source_url ?? null,
      due_date: f.due_date ?? null,
      status: f.status ?? null,
    })),
    teams: sim.teams.map((team) => ({
      id: team.id,
      name: team.name,
      throughput_data: team.throughput_data.join(', '),
      throughput_scale: team.throughput_scale,
      wip: team.wip,
      color: team.color ?? undefined,
      deliverables: team.deliverables.map((deliv) => ({
        id: deliv.id,
        feature_id: deliv.feature_id ?? null,
        name: deliv.name,
        unfinished_work_items: deliv.unfinished_work_items,
        unknown_min: deliv.unknown_min ?? null,
        unknown_max: deliv.unknown_max ?? null,
        due_date: deliv.due_date ?? '',
        available_to_start_min: deliv.available_to_start_min ?? '',
        available_to_start_max: deliv.available_to_start_max ?? '',
        predecessors: deliv.predecessors,
        source_id: deliv.source_id ?? null,
        source_url: deliv.source_url ?? null,
      })),
    })),
    num_simulations: sim.num_simulations,
    start_date: sim.start_date ?? '',
    seed: sim.seed?.toString() ?? '',
  };
}

// ============================================================================
// Feature → teams (shipcast-0eu)
// ============================================================================

/**
 * The teams a feature spans: every team with at least one deliverable grouped
 * under `featureId`, in form order, each listed once.
 *
 * Derived, not stored. A feature has no team of its own (it groups
 * deliverables across teams), so "which teams" is a read over
 * `teams[].deliverables[].feature_id` — the same state the deliverables
 * panel renders, which keeps the chips and the panel in agreement even
 * before a save lands. An ungrouped deliverable (`feature_id: null`) never
 * matches, whatever the caller passes.
 */
export function teamsForFeature(teams: TeamFormValues[], featureId: string): FeatureTeamRef[] {
  return teams
    .map((team) => ({
      id: team.id,
      name: team.name,
      color: team.color,
      // The same predicate the old `.some()` used — counted rather than
      // short-circuited, so the row can show how much of the feature sits
      // with each team for free.
      deliverableCount: team.deliverables.filter((d) => d.feature_id !== null && d.feature_id === featureId).length,
    }))
    .filter((team) => team.deliverableCount > 0);
}
